<template>
  <v-row class="ma-0">
    <!-- ===== Average Life ===== -->
    <v-col
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.DepositAverageLife') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in depositSections"
            :key="'life-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="depositModel[cat.code].life"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'yr'"
                @input="updateDeposit(cat.code, 'life', $event)"
              />
            </div>

            <v-slider
              :value="depositModel[cat.code].life"
              :min="depositModel[cat.code].minLife"
              :max="depositModel[cat.code].maxLife"
              :step="0.01"
              :color="getSliderColor('life', depositModel[cat.code].minLife)"
              :track-color="getSliderTrackColor('life', depositModel[cat.code].minLife)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('life', depositModel[cat.code].minLife)"
              @input="updateDeposit(cat.code, 'life', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- ===== Deposit Decay Rate ===== -->
    <v-col
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.DepositDecayRate') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in depositSections"
            :key="'rate-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="depositModel[cat.code].rate"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateDeposit(cat.code, 'rate', $event)"
              />
            </div>

            <v-slider
              :value="depositModel[cat.code].rate"
              :min="depositModel[cat.code].minRate"
              :max="depositModel[cat.code].maxRate"
              :step="0.01"
              :color="getSliderColor('rate', depositModel[cat.code].minRate)"
              :track-color="getSliderTrackColor('rate', depositModel[cat.code].minRate)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('rate', depositModel[cat.code].minRate)"
              @input="updateDeposit(cat.code, 'rate', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- ===== Deposit Up β ===== -->
    <v-col
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.DepositUpBeta') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in depositSections"
            :key="'up-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="depositModel[cat.code].upBeta"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateDeposit(cat.code, 'upBeta', $event)"
              />
            </div>

            <v-slider
              :value="depositModel[cat.code].upBeta"
              :min="depositModel[cat.code].minUpBeta"
              :max="depositModel[cat.code].maxUpBeta"
              :step="0.01"
              :color="getSliderColor('up', depositModel[cat.code].minUpBeta)"
              :track-color="getSliderTrackColor('up', depositModel[cat.code].minUpBeta)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('up', depositModel[cat.code].minUpBeta)"
              @input="updateDeposit(cat.code, 'upBeta', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- ===== Deposit Down β ===== -->
    <v-col
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.DepositDownBeta') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in depositSections"
            :key="'down-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="depositModel[cat.code].downBeta"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateDeposit(cat.code, 'downBeta', $event)"
              />
            </div>

            <v-slider
              :value="depositModel[cat.code].downBeta"
              :min="depositModel[cat.code].minDownBeta"
              :max="depositModel[cat.code].maxDownBeta"
              :step="0.01"
              :color="getSliderColor('down', depositModel[cat.code].minDownBeta)"
              :track-color="getSliderTrackColor('down', depositModel[cat.code].minDownBeta)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('down', depositModel[cat.code].minDownBeta)"
              @input="updateDeposit(cat.code, 'downBeta', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>

    <!-- ===== Deposit Reinvestment Spread (optional; e.g., TCD) ===== -->
    <v-col
      v-if="reinvestmentSections && reinvestmentSections.length"
      cols="12"
      class="py-3"
    >
      <div class="assumption-card">
        <div class="assumption-title">
          {{ $t('Assumptions.EditAssumptions.Tabs.DepositAssumptions.DepositsReinvestmentSpread') }}
        </div>

        <v-row class="ma-0">
          <v-col
            v-for="(cat, index) in reinvestmentSections"
            :key="'spread-' + cat.code"
            cols="12"
            md="6"
            :class="`pa-0 ${index % 2 === 0 ? 'pr-md-10' : ''} py-3`"
          >
            <div class="d-flex flex-row align-center justify-space-between">
              <div class="row-title">
                {{ cat.label }}
              </div>
              <v-text-field
                :value="reinvestmentModel[cat.code].spread"
                type="number"
                :step="0.01"
                hide-details
                dense
                outlined
                style="background:white;border:1px solid var(--v-synthesis-ui-grey-02-base);font-weight:400;"
                class="pct-input"
                :suffix="'%'"
                @input="updateReinvestment(cat.code, 'spread', $event)"
              />
            </div>

            <v-slider
              :value="reinvestmentModel[cat.code].spread"
              :min="reinvestmentModel[cat.code].minSpread"
              :max="reinvestmentModel[cat.code].maxSpread"
              :step="0.01"
              :color="getSliderColor('spread', reinvestmentModel[cat.code].minSpread)"
              :track-color="getSliderTrackColor('spread', reinvestmentModel[cat.code].minSpread)"
              hide-details
              class="flex-grow-1 slider-input"
              :style="getSliderStyle('spread', reinvestmentModel[cat.code].minSpread)"
              @input="updateReinvestment(cat.code, 'spread', $event)"
            />
          </v-col>
        </v-row>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

export default {
  name  : 'DepositAssumptions',
  mixins: [chartsColorMixin],
  props : {
    depositSections     : { type: Array, required: true },
    depositModel        : { type: Object, required: true },
    reinvestmentSections: { type: Array, default: () => [] },
    reinvestmentModel   : { type: Object, default: () => ({}) }
  },
  methods: {
    updateDeposit (categoryCode, field, value) {
      this.$emit('update-deposit', { categoryCode, field, value: Number(value) })
    },
    updateReinvestment (categoryCode, field, value) {
      this.$emit('update-reinvestment', { categoryCode, field, value: Number(value) })
    },

    // colors per group to match your visual language
    colorByGroup (group) {
      if (group === 'life') return this.themeColorRGBA('synthesis-ui-orange-02')
      if (group === 'rate') return this.themeColorRGBA('synthesis-ui-orange-02')
      if (group === 'up') return this.themeColorRGBA('synthesis-ui-purple-03')
      if (group === 'down') return this.themeColorRGBA('synthesis-ui-purple-03')
      if (group === 'spread') return this.themeColorRGBA('synthesis-ui-red-05')
      return this.themeColorRGBA('synthesis-ui-grey-06')
    },

    // Check if range is negative (min < 0)
    isNegativeRange (minValue) {
      return minValue < 0
    },

    // Get slider color - reversed when range is negative
    getSliderColor (group, minValue) {
      const baseColor = this.colorByGroup(group)
      return this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : baseColor
    },

    // Get track color - reversed when range is negative
    getSliderTrackColor (group, minValue) {
      return this.isNegativeRange(minValue) ? this.colorByGroup(group) : this.themeColorRGBA('synthesis-ui-grey-06')
    },

    // Get slider style - reversed when range is negative
    getSliderStyle (group, minValue) {
      const baseColor = this.colorByGroup(group)
      const color = this.isNegativeRange(minValue) ? this.themeColorRGBA('synthesis-ui-grey-06') : baseColor
      const thumbColor = this.isNegativeRange(minValue) ? baseColor : baseColor
      return {
        '--c'          : color,
        '--glow'       : this.withAlpha(color, 0.18),
        '--thumb-color': thumbColor,
        '--thumb-glow' : this.withAlpha(thumbColor, 0.32)
      }
    },

    // slider styling vars (thumb glow etc.)
    sliderStyle (color) {
      return {
        '--c'   : color,
        '--glow': this.withAlpha(color, 0.18)
      }
    },
    withAlpha (rgbaOrHex, a = 0.18) {
      // accepts rgba(...) or #hex; returns rgba with alpha
      if (rgbaOrHex.startsWith('rgba')) {
        return rgbaOrHex.replace(/rgba\(([^)]+)\)/, (_, rgb) => `rgba(${rgb.split(',').slice(0, 3).join(',')}, ${a})`)
      }
      const hex = rgbaOrHex.replace('#', '')
      const h = hex.length === 3 ? hex.split('').map(x => x + x).join('') : hex
      const n = parseInt(h, 16)
      const r = (n >> 16) & 255; const g = (n >> 8) & 255; const b = n & 255
      return `rgba(${r}, ${g}, ${b}, ${a})`
    }
  }
}
</script>

<style scoped>
.assumption-card{
  border: 1px solid #E6EEF5;
  background: #FFFFFF;
  border-radius: 12px;
  padding: 16px;
}
.assumption-title{
  font-size: 14px;
  font-weight: 600;
  color: #63747D;
  margin-bottom: 10px;
}
.row-title{
  font-size: 13px;
  font-weight: 600;
  color: #041F30;
  margin-bottom: 4px;
}

.pct-input{
  max-width: 95px;
  input {
    color: var(--v-synthesis-ui-grey-01-base);
  }
}

/* Hide number input spinners */
::v-deep .pct-input input[type="number"]::-webkit-outer-spin-button,
::v-deep .pct-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide number input spinners in Firefox */
::v-deep .pct-input input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

/* Style the suffix color */
::v-deep .pct-input .v-input__suffix,
::v-deep .pct-input .v-input__suffix .v-input__suffix-inner,
::v-deep .pct-input .v-input__append-inner {
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

/* Alternative approach - target the suffix text directly */
::v-deep .pct-input .v-text-field__suffix {
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

/* Root wrapper can stay minimal */
::v-deep .slider-input {
  background-color:inherit;
}

/* Track height + rounded pill look */
::v-deep .slider-input .v-slider__track-background,
.slider-input .v-slider__track-fill {
  height: 10px;                /* thicker track */
  border-radius: 6px;
}

/* Background track: white with subtle border and inset highlight */
::v-deep .slider-input .v-slider__track-background {               /* light pill */
  border: 1px solid #DDE6F1;                      /* subtle border */
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.02);     /* tiny inner highlight */
}

::v-deep .slider-input .v-slider__track-fill {
  border-radius: 2px;
  height: 10px;
}

::v-deep .slider-input .v-slider__thumb {
  margin-top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid #FFFFFF !important;                       /* white ring */
  background-color: var(--thumb-color, #3D68D5) !important;   /* use custom thumb color */
  box-shadow: 0px 0px 8px 0px var(--thumb-glow, rgba(61, 104, 213, 0.32)) !important;
  /* colored glow */
}

/* Remove default focus halo so our glow stands out */
    ::v-deep .slider-input .v-slider__thumb::before {
  display: none;
}

/* Optional: tighter container alignment */
::v-deep .slider-input .v-input__control,
.slider-input .v-input__slot { margin: 0; }

</style>
